import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { AddressStore } from '../../stores/AddressStore';
import { SessionStore } from '../../stores/SessionStore';
import { LocationStore } from '../../stores/LocationStore';
import { BasicInput } from '../basic-input';
import { Checkbox } from '../checkbox';
import { SecondaryButton } from '../secondary-button';
import { PrimaryButton } from '../primary-button';
import { BaseButton } from '../base-button';
import { Select } from '../select';
import { ERROR_CODES_MESSAGE } from '../../models/ApiError';
import { ADDRESS_TYPES } from '../../models/Address';
import { AddressHelper } from '../../helpers/AddressHelper';
import { enforceDepartments } from '../../hocs/enforce-departments';
import { StreetAutocompleteInputV2 } from '../street-autocomplete-input-v2';
import { GoogleMapComponent } from '../google-map-component';
import { Modal } from '../modal';
import { SimpleButton } from '../simple-button';
import { Spinner } from '../spinner';
import styles from './AddressFormV2.css';
import { ErrorApiHandler } from '../error-api-handler';

const IPropTypes = {
  collapseEarly: PropTypes.bool,
  onAddressCreation: PropTypes.func,
  onAddressEdition: PropTypes.func,
  reference: PropTypes.func,
  withoutTitle: PropTypes.bool,
  address: PropTypes.shape({
    id: PropTypes.string,
    addressType: PropTypes.string,
    streetName: PropTypes.string,
    additionalData: PropTypes.string,
    deliveryDisabled: PropTypes.bool,
    firstCorner: PropTypes.string,
    secondCorner: PropTypes.string,
    doorNumber: PropTypes.string,
    cityName: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    department: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
    bis: PropTypes.bool,
    apartment: PropTypes.string,
    letter: PropTypes.string,
    extraInfo1: PropTypes.shape({
      value: PropTypes.string,
      field: PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
      }),
    }),
    extraInfo2: PropTypes.shape({
      value: PropTypes.string,
      field: PropTypes.shape({
        id: PropTypes.number,
        label: PropTypes.string,
      }),
    }),
  }),
};

const defaultProps = {
  collapseEarly: false,
  onAddressCreation: () => {},
  onAddressEdition: () => {},
  reference: () => {},
  withoutTitle: false,
  address: null,
};

const ADDRESS_TYPE_OPTIONS = Object.values(ADDRESS_TYPES).map(addressType => ({
  label: AddressHelper.getAddressType(addressType),
  value: addressType,
}));

class AddressFormV2 extends React.Component {
  constructor(props) {
    super(props);
    const address = props.address || {};
    this.buttonSave = React.createRef();
    this.state = {
      mapCoordinates: address.coordinates,
      addressType: address.addressType || null,
      department: (address.department || {}).id || null,
      location: (address.cityName || {}).id || null,
      street: {
        id: address.street,
        name: address.streetName,
      },
      doorNumber: address.doorNumber || null,
      bis: address.bis || false,
      letter: address.letter || null,
      apartment: address.apartment || null,
      corner1: address.firstCornerId ? {
        id: address.firstCornerId,
        name: address.firstCorner,
      } : null,
      corner2: address.secondCornerId ? {
        id: address.secondCornerId,
        name: address.secondCorner,
      } : null,
      additionalData: address.additionalData,
      showDeleteModal: false,
    };

    this._onMapUpdate = this._onMapUpdate.bind(this);
    this._updateDataForm = this._updateDataForm.bind(this);
    this._updateDepartment = this._updateDepartment.bind(this);
    this._updateLocation = this._updateLocation.bind(this);

    this._toogleDeleteModal = this._toogleDeleteModal.bind(this);
    this._toggleErrorButton = this._toggleErrorButton.bind(this);
    this._toggleSuccessButton = this._toggleSuccessButton.bind(this);

    this._submitForm = this._submitForm.bind(this);
    this._createAddress = this._createAddress.bind(this);
    this._editAddress = this._editAddress.bind(this);
    this._deleteAddress = this._deleteAddress.bind(this);
  }

  componentDidMount() {
    if (this.state.department) {
      LocationStore.getLocations(this.state.department);
    }
  }

  componentWillUnmount() {
    LocationStore.clearLocations();
  }

  async _onMapUpdate({ lat, lng }) {
    await LocationStore.searchAddressesByPoint(lat, lng);
    const departmentId = LocationStore.departmentByPoint;
    const locationId = LocationStore.locationByPoint;
    if (departmentId !== this.state.department || locationId !== this.state.location) {
      this.setState({
        department: null,
        location: null,
        street: null,
        doorNumber: null,
        bis: false,
        letter: null,
        apartment: null,
        corner1: null,
        corner2: null,
      });
    }
    this.setState({
      mapCoordinates: {
        latitude: lat,
        longitude: lng,
      },
      department: departmentId,
      location: locationId,
    });
  }

  _updateDataForm(field, value) {
    if (field === 'bis') {
      this.setState(prevState => ({
        bis: !prevState.bis,
      }));
    } else {
      this.setState({
        [field]: value,
      });
    }
    if (field === 'location' || field === 'department') {
      this.setState({
        street: null,
        corner1: null,
        corner2: null,
      });
    }
    AddressStore.clearCreationAndEditionErrors();
  }

  _updateDepartment(department) {
    this.setState({
      department,
    });
    LocationStore.getLocations(department);
  }

  _updateLocation(locationId) {
    this.setState({
      location: locationId,
    });
  }

  _toogleDeleteModal(event, value) {
    event.preventDefault();
    this.setState({
      showDeleteModal: value,
    });
  }

  _toggleErrorButton(event) {
    event.preventDefault();
    this.setState({
      showDeleteModal: false,
    });

    AddressStore.clearDeletionErrors();
  }

  _toggleSuccessButton(event) {
    event.preventDefault();
    this.setState({
      showDeleteModal: false,
    });

    AddressStore.clearDeletionSuccess();

    this.props.onAddressEdition();
  }

  _canSubmitAddress() {
    return !AddressStore.loadingCreation &&
      !AddressStore.loadingEdition &&
      this.state.addressType &&
      this.state.department &&
      this.state.location &&
      this.state.street &&
      this.state.corner1;
  }

  _getCommonAddressFields() {
    const selectedAddressType = ADDRESS_TYPE_OPTIONS.find(addressType => (
      addressType.value === this.state.addressType
    ));
    return {
      addressType: selectedAddressType && selectedAddressType.label,
      department: this.state.department,
      location: this.state.location,
      street: this.state.street,
      doorNumber: this.state.doorNumber || 0,
      bis: this.state.bis,
      letter: this.state.letter,
      apartment: this.state.apartment,
      corner1: this.state.corner1,
      corner2: this.state.corner2,
      additionalData: this.state.additionalData,
      userId: SessionStore.user.id,
      userSetCoordinates: this.state.mapCoordinates,
      coordinates: this.state.mapCoordinates,
    };
  }

  _submitForm(event) {
    event.preventDefault();
    if (this.props.address === null) {
      this._createAddress();
      return;
    }
    this._editAddress();
  }

  async _createAddress() {
    AddressStore.clearCreationAndEditionErrors();
    const newAddressId = await AddressStore.createAddressV2(this._getCommonAddressFields());
    if (!AddressStore.creationError) {
      this.props.onAddressCreation(newAddressId);
    }
  }

  async _editAddress() {
    AddressStore.clearCreationAndEditionErrors();
    const commonFields = this._getCommonAddressFields();
    const updatedAddressId = await AddressStore.editAddressV2({
      ...commonFields,
      id: this.props.address.id,
    });
    if (!AddressStore.editionError) {
      this.props.onAddressEdition(updatedAddressId);
    }
  }

  _deleteAddress(event) {
    event.preventDefault();
    AddressStore.deleteAddress(this.props.address.id);
  }

  render() {
    const departmentOptions = LocationStore.departments.map(departmentData => ({
      value: departmentData.id,
      label: departmentData.name,
    }));
    const selectedDepartment = departmentOptions.find(department => (
      department.value === this.state.department
    ));
    const locationOptions = (LocationStore.locations || []).map(locationData => ({
      value: locationData.id,
      label: locationData.name,
    }));
    const selectedLocation = locationOptions.find(location => (
      location.value === this.state.location
    ));
    const selectedAddressType = ADDRESS_TYPE_OPTIONS.find(addressType => (
      addressType.value === this.state.addressType
    ));
    const streetError = this.state.street === '' ? 'Este campo no puede ser vacío' : null;
    const corner1Error = this.state.corner1 === '' ? 'Este campo no puede ser vacío' : null;

    return (
      <div ref={this.props.reference}>
        {
          !this.props.withoutTitle &&
          <h2 className={styles.title}>
            Nueva dirección
          </h2>
        }
        <div className={styles.blockAddressText}>
          Selecciona tu dirección en el mapa para desbloquear el formulario.
          Puedes utilizar el botón `Mi ubicación` para localizar tu zona.
        </div>
        <form
          className={[styles.form, this.props.collapseEarly ? styles.earlyCollapse : ''].join(' ')}
          onSubmit={this._submitForm}
        >
          <div className={styles.mapSection}>
            <GoogleMapComponent
              modeEdit={!!this.props.address}
              center={
                this.state.mapCoordinates ?
                  {
                    lat: this.state.mapCoordinates.latitude,
                    lng: this.state.mapCoordinates.longitude,
                  } : undefined}
              onSuccess={this._onMapUpdate}
            />
          </div>
          <div className={styles.inputSection}>
            { AddressStore.creationError &&
              <ErrorApiHandler
                message={AddressStore.creationError.message}
                content={AddressStore.creationError.content}
              />
            }
            { AddressStore.editionError &&
              <ErrorApiHandler
                message={AddressStore.editionError.message}
                content={AddressStore.editionError.content}
              />
            }
            {!this.state.mapCoordinates &&
              <div className={this.props.address !== null ?
                styles.blockAddressEdit : styles.blockAddressNew}
              />
            }
            {
              !!this.props.address &&
              <div className={styles.deleteButtonContainer}>
                <BaseButton
                  className={styles.deleteButton}
                  onClick={ev => this._toogleDeleteModal(ev, true)}
                >
                  Borrar dirección
                </BaseButton>
              </div>
            }
            <div className={styles.inputGroupFlex}>
              <div className={styles.selectContainer}>
                <label className={styles.label}>
                  Departamento *
                </label>
                <Select
                  className={styles.select}
                  onChange={this._updateDepartment}
                  options={departmentOptions}
                  value={selectedDepartment ? selectedDepartment.label : 'Seleccionar'}
                />
                {/* <div className={styles.readonlyValue}>
                  {selectedDepartment ? selectedDepartment.label : 'Seleccionar'}
                </div> */}
              </div>
              <div className={styles.selectContainer}>
                <label className={styles.label}>
                  Localidad *
                </label>
                <Select
                  className={styles.select}
                  disabled={!LocationStore.locations}
                  onChange={this._updateLocation}
                  options={locationOptions}
                  value={selectedLocation ? selectedLocation.label : 'Seleccionar'}
                />
                {/* <div className={styles.readonlyValue}>
                  {selectedLocation ? selectedLocation.label : 'Seleccionar'}
                  </div> */}
              </div>
            </div>
            <div className={styles.selectContainer}>
              <label className={styles.label}>
                Tipo *
              </label>
              <Select
                className={styles.select}
                disabled={!this.state.department || !this.state.location}
                onChange={value => this._updateDataForm('addressType', value)}
                options={ADDRESS_TYPE_OPTIONS}
                value={selectedAddressType ? selectedAddressType.label : 'Seleccionar'}
              />
            </div>
            <StreetAutocompleteInputV2
              className={styles.input}
              currentDepartment={selectedDepartment ? selectedDepartment.label : null}
              currentLocation={selectedLocation ? selectedLocation.label : null}
              currentLocationid={this.state.location}
              disabled={!this.state.department || !this.state.location}
              error={streetError}
              onChange={value => this._updateDataForm('street', value)}
              label="Calle *"
              placeholder="Solo calle, sin número de puerta"
              name="street"
              value={this.state.street ? this.state.street.name : ''}
            />
            <div className={styles.inputGroupFlex}>
              <BasicInput
                className={styles.input}
                disabled={!this.state.department || !this.state.location}
                label="N° de puerta"
                name="doorNumber"
                placeholder="Si no tiene N°, poner 0"
                onChange={ev => this._updateDataForm('doorNumber', (ev.target.value || '').replace(/\D/, ''))}
                value={this.state.doorNumber || ''}
              />
              <div className={styles.checkbox}>
                <div>
                  <label className={styles.label}>
                    Bis
                  </label>
                </div>
                <Checkbox
                  disabled={!this.state.department || !this.state.location}
                  checked={this.state.bis}
                  className={styles.bisCheckbox}
                  onClick={value => this._updateDataForm('bis', value)}
                />
              </div>
              <BasicInput
                className={styles.input}
                disabled={!this.state.department || !this.state.location}
                label="Letra"
                name="letter"
                onChange={ev => this._updateDataForm('letter', ev.target.value)}
                value={this.state.letter || ''}
              />
              <BasicInput
                className={styles.input}
                disabled={!this.state.department || !this.state.location}
                label="Apto"
                name="apartment"
                onChange={ev => this._updateDataForm('apartment', ev.target.value)}
                value={this.state.apartment || ''}
              />
            </div>
            <div className={styles.inputGroupFlex}>
              <StreetAutocompleteInputV2
                className={styles.input}
                currentDepartment={selectedDepartment ? selectedDepartment.label : null}
                currentLocation={selectedLocation ? selectedLocation.label : null}
                currentLocationid={this.state.location}
                disabled={!this.state.department || !this.state.location}
                error={corner1Error}
                onChange={value => this._updateDataForm('corner1', value)}
                label="Primera esquina *"
                name="corner1"
                value={this.state.corner1 ? this.state.corner1.name : ''}
              />
              <StreetAutocompleteInputV2
                className={styles.input}
                currentDepartment={selectedDepartment ? selectedDepartment.label : null}
                currentLocation={selectedLocation ? selectedLocation.label : null}
                currentLocationid={this.state.location}
                disabled={!this.state.department || !this.state.location || !this.state.corner1}
                onChange={value => this._updateDataForm('corner2', value)}
                label="Segunda esquina"
                name="corner2"
                value={this.state.corner2 ? this.state.corner2.name : ''}
              />
            </div>
            <div className={styles.textareaContainer}>
              <label className={styles.label}>
                Información adicional
              </label>
              <span className={styles.infoLabel}>
                Por favor, no incluya nada sobre horarios en este campo
              </span>
              <textarea
                maxLength="300"
                className={styles.textarea}
                disabled={!this.state.department || !this.state.location}
                onChange={ev => this._updateDataForm('additionalData', ev.target.value)}
                value={this.state.additionalData || ''}
              />
            </div>
            {
              AddressStore.creationError &&
              AddressStore.creationError.code &&
              <p className={styles.error}>
                {/* eslint-disable-next-line no-nested-ternary */}
                {ERROR_CODES_MESSAGE[AddressStore.creationError.code] ?
                    ERROR_CODES_MESSAGE[AddressStore.creationError.code] :
                    AddressStore.creationError.message ?
                        AddressStore.creationError.message :
                        'Ha ocurrido un error, por favor contactá con nuestra Atención al Cliente al 29027737'}
              </p>
            }
            {
              !this.props.address &&
              <div className={styles.buttonContainer}>
                <PrimaryButton
                  className={styles.newButton}
                  disabled={!this._canSubmitAddress()}
                >
                  Agregar Dirección
                </PrimaryButton>
              </div>
            }
            {
              !!this.props.address &&
              <div className={styles.editButtonContainer}>
                <SecondaryButton
                  className={styles.saveButton}
                  disabled={!this._canSubmitAddress()}
                  ref={this.buttonSave}
                >
                  Guardar
                </SecondaryButton>
              </div>
            }
          </div>
        </form>
        {
          this.state.showDeleteModal &&
          <Modal>
            <div className={styles.modalChildren}>
              {
                AddressStore.deletionError &&
                <div>
                  <p className={styles.modalError}>
                    Ha ocurrido un error, por favor intente nuevamente.
                  </p>
                  <SimpleButton
                    onClick={this._toggleErrorButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                AddressStore.deletionInternalError &&
                <div>
                  <p className={styles.modalError}>
                    Estamos experimentando algunas dificultades en nuestro sitio.
                    Por favor intente nuevamente en unos momentos.
                  </p>
                  <SimpleButton
                    onClick={this._toggleErrorButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                AddressStore.deletionSuccess &&
                <div>
                  <p className={styles.modalSuccess}>
                    Dirección eliminada con éxito
                  </p>
                  <SimpleButton
                    onClick={this._toggleSuccessButton}
                    className={styles.messageModalButton}
                  >
                    Volver
                  </SimpleButton>
                </div>
              }
              {
                AddressStore.deletingAddress &&
                <Spinner className={styles.modalSpinner} />
              }
              {
                !AddressStore.deletionInternalError &&
                !AddressStore.deletionError &&
                !AddressStore.deletingAddress &&
                !AddressStore.deletionSuccess &&
                <div>
                  <p className={styles.modalQuestion}>
                    ¿Está seguro que desea borrar esta dirección?
                  </p>
                  <p className={styles.modalText}>
                    Esta operación no puede deshacerse.
                  </p>
                  <div className={styles.modalButtonsSection}>
                    <SimpleButton
                      className={styles.hideModalButton}
                      onClick={ev => this._toogleDeleteModal(ev, false)}
                    >
                      Cancelar
                    </SimpleButton>
                    <SimpleButton
                      className={styles.deleteModalButton}
                      onClick={this._deleteAddress}
                    >
                      Borrar dirección
                    </SimpleButton>
                  </div>
                </div>
              }
            </div>
          </Modal>
        }
      </div>
    );
  }
}

AddressFormV2.propTypes = IPropTypes;
AddressFormV2.defaultProps = defaultProps;

const ObservedAddressForm = enforceDepartments(observer(AddressFormV2));

export { ObservedAddressForm as AddressFormV2 };



// WEBPACK FOOTER //
// ./src/common/address-form-v2/AddressFormV2.jsx